import { Armchair, Cube, MagnifyingGlass, Monitor, Plant, SquaresFour, UploadSimple, Wall, X } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { ASSETS } from '../data/assets';
import type { AssetCategory, AssetDefinition } from '../types';
import { LazyAssetThumbnail } from './LazyAssetThumbnail';

const categories: AssetCategory[] = ['전체', '가구', '디스플레이', '부스 구조', '장식'];

function CategoryIcon({ category }: { category: AssetCategory }) {
  if (category === '가구') return <Armchair size={21} weight="duotone" />;
  if (category === '디스플레이') return <Monitor size={21} weight="duotone" />;
  if (category === '장식') return <Plant size={21} weight="duotone" />;
  if (category === '부스 구조') return <Wall size={21} weight="duotone" />;
  return <SquaresFour size={21} weight="duotone" />;
}

interface LibraryPanelProps {
  onAdd: (asset: AssetDefinition) => void;
  onUpload: () => void;
}

export function LibraryPanel({ onAdd, onUpload }: LibraryPanelProps) {
  const [category, setCategory] = useState<AssetCategory>('전체');
  const [search, setSearch] = useState('');
  const [browserOpen, setBrowserOpen] = useState(false);
  const filtered = useMemo(() => ASSETS.filter((asset) => {
    const categoryMatches = category === '전체' || asset.category === category;
    const queryMatches = `${asset.name} ${asset.description}`.toLowerCase().includes(search.toLowerCase());
    return categoryMatches && queryMatches;
  }), [category, search]);

  const chooseCategory = (item: AssetCategory) => {
    setCategory(item);
    setBrowserOpen(true);
  };

  const formatMillimeters = (value: number) => Math.round(value * 1000).toLocaleString('ko-KR');

  return (
    <aside className={`library-panel ${browserOpen ? 'browser-open' : ''}`} aria-label="오브젝트 라이브러리">
      <nav className="library-category-rail" aria-label="오브젝트 카테고리">
        <div className="library-rail-title"><Cube size={19} weight="duotone" /><span>오브젝트</span></div>
        <div className="library-category-buttons" role="tablist" aria-orientation="vertical">
          {categories.map((item) => (
            <button key={item} type="button" role="tab" aria-selected={category === item} className={category === item ? 'active' : ''} onClick={() => chooseCategory(item)}>
              <CategoryIcon category={item} />
              <span>{item}</span>
              <small>{item === '전체' ? ASSETS.length : ASSETS.filter((asset) => asset.category === item).length}</small>
            </button>
          ))}
        </div>
      </nav>

      <section className="library-browser" aria-label={`${category} 오브젝트`}>
        <header className="library-browser-header">
          <div><span>카테고리</span><strong>{category}</strong></div>
          <div className="library-header-actions">
            <button className="library-upload-button" type="button" onClick={onUpload} title="내 3D 모델 등록">
              <UploadSimple size={16} />
              <span>내 모델 등록</span>
            </button>
            <button className="library-browser-close" type="button" onClick={() => setBrowserOpen(false)} aria-label="오브젝트 목록 닫기"><X size={17} /></button>
          </div>
        </header>
        <label className="library-search">
          <MagnifyingGlass size={16} aria-hidden="true" />
          <span className="sr-only">오브젝트 검색</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="오브젝트 검색" />
        </label>
        <div className="library-result-meta"><span><strong>{filtered.length}</strong>개 모델</span><small>클릭 또는 드래그</small></div>
        <div className="library-assets">
          {filtered.map((asset, index) => (
            <article
              className="library-asset-card"
              key={asset.kind}
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData('application/x-expospace-asset', asset.kind);
                event.dataTransfer.effectAllowed = 'copy';
              }}
            >
              <button type="button" onClick={() => onAdd(asset)}>
                <span className="library-asset-visual">
                  <LazyAssetThumbnail asset={asset} />
                  <span className="asset-sequence" aria-label={`${category} 목록 번호 ${index + 1}`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </span>
                <span className="library-asset-copy">
                  <strong>{asset.name}</strong>
                  <dl className="asset-dimensions" aria-label={`${asset.name} 실측 크기`}>
                    <div><dt>폭</dt><dd>{formatMillimeters(asset.dimensions.width)} mm</dd></div>
                    <div><dt>높이</dt><dd>{formatMillimeters(asset.dimensions.height)} mm</dd></div>
                    <div><dt>깊이</dt><dd>{formatMillimeters(asset.dimensions.depth)} mm</dd></div>
                  </dl>
                </span>
              </button>
            </article>
          ))}
          {!filtered.length && <div className="library-empty"><Cube size={25} /><strong>결과가 없습니다</strong><span>검색어나 카테고리를 바꿔보세요.</span></div>}
        </div>
      </section>
    </aside>
  );
}
